import type { RefObject } from 'react';
import { faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { Measurable } from '@radix-ui/rect';

import type {
  FormBlockBackground,
  FormBlockHeadingAlignment,
} from '@/components/blocks/primitives/form-block';
import { FloatWrapControl } from '@/components/page-editor/settings-panel/float-wrap-control';
import { useBlockFormDraftSync } from '@/components/shared/use-block-form-draft-sync';
import { Button } from '@/components/ui/button';
import { SelectInput, TextInput } from '@/components/ui/form';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Heading5 } from '@/components/ui/typography';
import { usePopoverPlacement } from '@/hooks/use-popover-placement';
import type { GroupChildFloat } from '@/lib/blocks/layout/group-layout';
import { scheduleScrollToPageEditorBlock } from '@/lib/page-editor/scroll-to-block';
import { getBlockUiKey, type PageBlock } from '@/types/block';

const BACKGROUND_OPTIONS = [
  { value: 'white', title: 'White background' },
  { value: 'transparent', title: 'Transparent' },
];

const HEADING_ALIGNMENT_OPTIONS = [
  { value: 'left', title: 'Left' },
  { value: 'center', title: 'Center' },
];

interface FormBlockEditorPopoverProps {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  showFloatOption?: boolean;
}

export function FormBlockEditorPopover({
  sectionId,
  block,
  open,
  onOpenChange,
  anchorRef,
  showFloatOption = false,
}: FormBlockEditorPopoverProps) {
  const { draft, forms, loading, formOptions, updateDraft, handleFormChange, handleClose } =
    useBlockFormDraftSync({ sectionId, block, open, onOpenChange });

  const { side, sideOffset } = usePopoverPlacement(anchorRef, open, 380, 0.5);
  const blockUiKey = getBlockUiKey(block);

  const handleFloatChange = (float: GroupChildFloat | null) => {
    if (float === (draft.float ?? null)) return;
    updateDraft({ float });
    scheduleScrollToPageEditorBlock(blockUiKey);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      {anchorRef ? <PopoverAnchor virtualRef={anchorRef as RefObject<Measurable>} /> : null}
      <PopoverContent
        side={side}
        align="start"
        sideOffset={sideOffset}
        avoidCollisions
        collisionPadding={16}
        hideWhenDetached
        updatePositionStrategy={showFloatOption ? 'always' : 'optimized'}
        className="z-[130] w-96 overflow-hidden border bg-background"
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onInteractOutside={(event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest('[data-slot="select-content"]')) {
            event.preventDefault();
          }
        }}
      >
        <Button
          size="icon"
          variant="ghost"
          colorScheme="secondary"
          onClick={handleClose}
          className="h-8 w-8 absolute top-4 right-4 z-50 bg-background"
        >
          <Icon icon={faXmark} />
          <span className="sr-only">Close</span>
        </Button>

        <div className="flex h-full flex-col">
          <div className="min-h-0 flex-1 max-h-[min(50vh,380px)] overflow-y-auto space-y-4 px-2">
            <Heading5 className="text-base mb-4">Select Form</Heading5>

            <SelectInput
              label="Form"
              value={draft.formId ?? ''}
              onValueChange={handleFormChange}
              placeholder={loading ? 'Loading forms…' : 'Select a form'}
              emptyText="No forms found."
              searchable={forms.length > 5}
              searchPlaceholder="Search forms…"
              options={formOptions}
              contentClassName="z-[150]"
            />

            <TextInput
              label="Submit button label"
              value={draft.submitLabel ?? ''}
              onChange={(e) => updateDraft({ submitLabel: e.target.value || undefined })}
              placeholder="Submit"
            />

            <SelectInput
              label="Background"
              value={draft.background ?? 'white'}
              onValueChange={(value) => updateDraft({ background: value as FormBlockBackground })}
              searchable={false}
              options={BACKGROUND_OPTIONS}
              contentClassName="z-[150]"
            />

            <SelectInput
              label="Heading alignment"
              value={draft.headingAlignment ?? 'left'}
              onValueChange={(value) =>
                updateDraft({ headingAlignment: value as FormBlockHeadingAlignment })
              }
              searchable={false}
              options={HEADING_ALIGNMENT_OPTIONS}
              contentClassName="z-[150]"
            />

            {showFloatOption && (
              <FloatWrapControl
                value={draft.float ?? null}
                onChange={handleFloatChange}
                description="Float this form so surrounding content in the same group wraps around it"
              />
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
